import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, Input, LoadingSpinner, Select } from '@/components/common';
import { useIntegrationStatus } from '@/hooks/useInsurers';
import { getErrorMessage } from '@/services/api';

/** Same colours as the Insurers screen (#385). */
const STATE_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  active: 'success',
  configured_untested: 'warning',
  test_failed: 'error',
  not_configured: 'default',
};

const STATE_OPTIONS = [
  { value: '', label: 'All API states' },
  { value: 'active', label: 'Active' },
  { value: 'configured_untested', label: 'Configured, not tested' },
  { value: 'test_failed', label: 'Test failed' },
  { value: 'not_configured', label: 'Not configured' },
];

const ADAPTER_OPTIONS = [
  { value: '', label: 'With or without adapter' },
  { value: 'yes', label: 'Adapter in code' },
  { value: 'no', label: 'No adapter yet' },
];

const OPERATION_LABELS: Record<string, string> = {
  quote: 'Quote',
  proposal: 'Proposal',
  payment_initiate: 'Payment',
  issue: 'Issue',
  policy_pdf: 'PDF',
  policy_status: 'Status',
  endorsement: 'Endorse',
  claim_intimation: 'Claim',
  cancel: 'Cancel',
};

const NOT_IN_BASE_LABELS: Record<string, string> = {
  renewal_quote: 'renewal quote',
  renewal_issue: 'renewal issue',
  break_in_inspection: 'break-in inspection',
  ekyc_ckyc: 'eKYC / CKYC',
  claims_view: 'claims view',
  account_balance_cd_pd: 'CD/PD balance',
  master_sync: 'master sync',
};

const labelOf = (op: string) => OPERATION_LABELS[op] ?? op;

/**
 * #559: the country admin's integration status dashboard.
 *
 * Replaces the status spreadsheets with what the app can prove: the derived API
 * status of the Insurers screen (#385) and the capability matrix read from the
 * adapter code (#554). "In code" never means live; UAT or production proof is
 * not stored anywhere yet.
 */
export function IntegrationStatusPage() {
  const { data, isLoading, error } = useIntegrationStatus();
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [adapterFilter, setAdapterFilter] = useState('');

  const insurers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data?.insurers ?? []).filter((row) => {
      if (term && !`${row.name} ${row.code} ${row.drive_code}`.toLowerCase().includes(term)) return false;
      if (stateFilter && row.api_status.state !== stateFilter) return false;
      if (adapterFilter === 'yes' && !row.has_adapter) return false;
      if (adapterFilter === 'no' && row.has_adapter) return false;
      return true;
    });
  }, [data, search, stateFilter, adapterFilter]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <p role="alert" className="text-red-600 dark:text-red-400">
          {error ? getErrorMessage(error) : 'Integration status is not available.'}
        </p>
      </Card>
    );
  }

  const { summary, operations } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Integration status</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            API status per insurer and what each adapter implements in code. This is not the broker&apos;s
            &quot;Active&quot; connection on the Insurers screen: that says the broker trades with the insurer, this
            says the API is stored, credentialed and tested.{' '}
            <Link to="/admin/insurers" className="text-primary-600 hover:underline">
              Back to Insurance Companies
            </Link>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-6" data-testid="integration-summary">
        {[
          ['Insurers', summary.total],
          ['Adapter in code', summary.with_adapter],
          ['Active', summary.active],
          ['Configured, not tested', summary.configured_untested],
          ['Test failed', summary.test_failed],
          ['Not configured', summary.not_configured],
        ].map(([label, value]) => (
          <Card key={String(label)}>
            <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-white">{value ?? 0}</p>
          </Card>
        ))}
      </div>

      <Card>
        <div className="grid gap-3 md:grid-cols-3">
          <Input
            placeholder="Search name or code"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search insurers"
          />
          <Select options={STATE_OPTIONS} value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} />
          <Select options={ADAPTER_OPTIONS} value={adapterFilter} onChange={(e) => setAdapterFilter(e.target.value)} />
        </div>
      </Card>

      <Card padding="sm" className="overflow-x-auto">
        {/* #638: tight cells so the whole matrix, Cancel included, fits at 1440px. */}
        <table className="min-w-full text-sm" data-testid="integration-matrix">
          <thead>
            <tr className="text-left text-gray-500 dark:text-gray-400">
              <th className="px-2 py-2">Insurer</th>
              <th className="px-2 py-2">API status</th>
              <th className="px-2 py-2">Line</th>
              {operations.map((op) => (
                <th key={op} className="px-1.5 py-2 text-center whitespace-nowrap">
                  {labelOf(op)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {insurers.length === 0 && (
              <tr>
                <td colSpan={3 + operations.length} className="px-3 py-6 text-center text-gray-500">
                  No insurers match.
                </td>
              </tr>
            )}
            {insurers.flatMap((row) => {
              const lines = row.lines.length ? row.lines : [null];
              return lines.map((line, index) => (
                <tr key={`${row.id}-${line?.line ?? 'none'}`} className="border-t border-gray-200 dark:border-gray-700">
                  {index === 0 && (
                    <>
                      <td className="px-2 py-2 align-top max-w-[15rem]" rowSpan={lines.length}>
                        <div className="font-medium text-gray-900 dark:text-white">{row.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{row.code}</div>
                        {!row.has_adapter && row.expected_lines.length > 0 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Expected: {row.expected_lines.join(', ')} ({row.expected_lines_basis})
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top" rowSpan={lines.length}>
                        <Badge variant={STATE_VARIANT[row.api_status.state] ?? 'default'} size="sm">
                          {row.api_status.label}
                        </Badge>
                        {row.api_status.last_tested_at && (
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Tested {new Date(row.api_status.last_tested_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                    </>
                  )}
                  {line ? (
                    <>
                      <td className="px-2 py-2 capitalize">{line.line}</td>
                      {operations.map((op) => (
                        <td key={op} className="px-1.5 py-2 text-center">
                          {line.operations[op] ? (
                            <span aria-label={`${labelOf(op)}: in code`} title="Implemented in code">
                              Yes
                            </span>
                          ) : (
                            <span className="text-gray-400" aria-label={`${labelOf(op)}: not built`} title="Not built">
                              -
                            </span>
                          )}
                        </td>
                      ))}
                    </>
                  ) : (
                    <td colSpan={1 + operations.length} className="px-3 py-2 text-gray-500 dark:text-gray-400">
                      No adapter in code
                    </td>
                  )}
                </tr>
              ));
            })}
          </tbody>
        </table>
      </Card>

      {(data.plugins_without_insurer ?? []).length > 0 && (
        <Card>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Adapters with no insurer record</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            These adapters exist in code but no insurer row points at them, so they cannot be selected for a broker
            until the insurer record is created.
          </p>
          <ul className="mt-2 space-y-1 text-sm text-gray-900 dark:text-white" data-testid="orphan-plugins">
            {(data.plugins_without_insurer ?? []).map((p) => (
              <li key={p.code}>
                {p.name} <span className="text-gray-500 dark:text-gray-400">({p.code}; {p.lines.join(', ')})</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <p className="text-sm text-gray-500 dark:text-gray-400">
        &quot;Yes&quot; means the adapter implements the operation in code, not that it works in the insurer&apos;s UAT or
        production. Operations no adapter can offer yet:{' '}
        {data.not_in_base.map((op) => NOT_IN_BASE_LABELS[op] ?? op).join(', ')}.
      </p>
    </div>
  );
}
